import { Toast } from 'vant'

import { isProduction } from '@/data/runtime'
import { logger } from '@/define'

import { delay } from './Thread'

export declare type ClassIntf<T> = { new (...args: Array<any>): T }

export declare type SetupClassIntf<T extends { setup: Function }> = { new (...args: Array<any>): T }

/** 装饰器 - 启动一个 class
 * @description 注意 class 里面需要有个 setup() 方法
 */
export const setup = () => {
    return function <T extends { setup: Function }>(constructor: SetupClassIntf<T>) {
        new constructor().setup()
    }
}

/** 方法执行时间分析 */
export const executeTime = (description: string = '耗时') => {
    return function (_target: any, _methodName: any, desc: any) {
        if (isProduction) return
        // 1. 保存当前的方法
        const oMethod = desc.value
        // 2. 改写方法
        desc.value = async function (...params: Array<any>) {
            try {
                console.time(description)
                await oMethod.apply(this, params)
            } finally {
                console.timeEnd(description)
            }
        }
    }
}

const debounceMap: { [key: string]: number } = {}

/** 防抖操作 (同时, 仅允许一次操作执行)
 *
 * @description 注意: 此行为全局唯一, 相对来讲, 如果使用此装饰器, 则建议仅对唯一操作如: 提交表单场景 使用
 *
 * @param {number} delay 下次执行间隔时间
 */
export const debounce = (delayTime: number = 100, custom?: string) => {
    return function (_target: any, _methodName: any, desc: any) {
        let timer: any
        let methodName!: string

        if (custom) methodName = custom
        else if (_methodName) methodName = _target + ':' + _methodName
        else methodName = 'default'

        // 1. 保存当前的方法
        const oMethod = desc.value
        // 2. 改写方法
        desc.value = async function (...params: Array<any>) {
            if (!debounceMap[methodName]) debounceMap[methodName] = 0
            if (debounceMap[methodName] > 0) return
            try {
                debounceMap[methodName]++
                await oMethod.apply(this, params)
                await delay(delayTime)
            } finally {
                if (debounceMap[methodName] > 0) debounceMap[methodName]--
                clearTimeout(timer)
            }
        }
    }
}

/** Loading 工具 */
export const loading = async (asyncFunc: Function, tipError?: boolean, message?: string) => {
    Toast.loading({
        message: message ?? '加载中...',
        forbidClick: true,
        duration: 0
    })
    try {
        await asyncFunc()
    } catch (e) {
        console.error(e)
        if (tipError) {
            Toast((e as Error)?.message)
            await delay(1500)
        }
        throw e
    } finally {
        Toast.clear(true)
    }
}

/** 切换数据状态
 * @description 用于 loading, hidden 等状态切换
 */
export const toggleStatus = (prop: string, status: Array<any> = [true, false], minTime: number = 750) => {
    return function (_target: any, _methodName: any, desc: any) {
        // 1. 保存当前的方法
        const oMethod = desc.value
        // 2. 改写方法
        desc.value = async function (...params: Array<any>) {
            status = status?.length === 2 ? status : [true, false]
            this[prop] = status[0]
            try {
                // 调用未改写的方法
                return await oMethod.apply(this, params)
            } finally {
                await delay(minTime)
                this[prop] = status[1]
            }
        }
    }
}

/** 错误提示 */
export const errorTips = (unknowErrorMessage?: string) => {
    return function (_target: any, _methodName: any, desc: any) {
        // 1. 保存当前的方法
        const oMethod = desc.value
        // 2. 改写方法
        desc.value = async function (...params: Array<any>) {
            try {
                return await oMethod.apply(this, params)
            } catch (e) {
                const errMsg: string = (e as Error)?.message ?? unknowErrorMessage ?? '出错了'
                if (typeof errMsg === 'string' && /[\u4e00-\u9fa5]/.test(errMsg)) {
                    Toast(errMsg)
                } else {
                    Toast('出错了')
                }
                throw e
            }
        }
    }
}

/** 仅捕获异常
 *
 * @param {string} construtor 模块,类名
 * @param {boolean} onlyCapture 仅捕获, 不上报
 * @returns
 */
export const captureException = (construtor?: string, onlyCapture?: boolean) => {
    return function (_target: any, method: any, desc: any) {
        // 1. 保存当前的方法
        const oMethod = desc.value
        // 2. 改写方法
        desc.value = async function (...params: Array<any>) {
            try {
                return await oMethod.apply(this, params)
            } catch (e) {
                // > report, 注: logger.error 接口 会去获取平台信息. 等一些公共参数
                if (process.env.command === 'serve') {
                    console.error({
                        e,
                        tags: { construtor, method },
                        extra: { params }
                    })
                } else if (!onlyCapture) {
                    logger.captureException(e as Error, {
                        tags: { construtor, method },
                        extra: { params }
                    })
                }
            }
        }
    }
}
